import {
  forwardRef,
  ForwardRefExoticComponent,
  MouseEvent,
  ReactElement,
  Ref,
  useCallback,
  useMemo,
} from 'react';
import {
  LinkProps,
  useHref,
  useLinkClickHandler,
  useLocation,
  useResolvedPath,
  NavLinkProps,
} from 'react-router-dom';
import { Link as ComponentsLink } from '@robocorp/components';
import { AnyProps, ExtractProps } from '~/types';

type NavigateProps = Pick<LinkProps, 'onClick' | 'replace' | 'state' | 'target' | 'to'>;

/**
 * Returns link navigation props to handle navigation with react router
 *
 * Partially taken from:
 * https://github.com/remix-run/react-router/blob/main/packages/react-router-dom/index.tsx#L406
 */
const useNavigate = ({ onClick, state, target, to, replace = false }: NavigateProps) => {
  const href = useHref(to);
  const internalOnClick = useLinkClickHandler(to, { replace, state, target });
  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (onClick) onClick(event);
      if (!event.defaultPrevented) {
        internalOnClick(event);
      }
    },
    [internalOnClick, onClick],
  );

  return useMemo(() => ({ href, onClick: handleClick }), [href, handleClick]);
};

type DefaultProps = ExtractProps<typeof ComponentsLink>;

type RouterLinkProps<T extends AnyProps> = {
  // eslint-disable-next-line react/require-default-props
  component?: ForwardRefExoticComponent<ExtractProps<T>>;
} & NavigateProps &
  Omit<ExtractProps<T>, 'href'>;

/**
 * Passes react router Link props to custom component
 */
const BaseRouterLink = <T extends AnyProps, K = unknown>(
  {
    component: Component = ComponentsLink as ForwardRefExoticComponent<ExtractProps<T>>,
    replace,
    to,
    state,
    target,
    onClick,
    ...rest
  }: RouterLinkProps<T>,
  forwardedRef: Ref<K>,
) => {
  const navigateProps = useNavigate({ replace, to, state, target, onClick });
  return <Component {...navigateProps} {...(rest as ExtractProps<T>)} ref={forwardedRef} />;
};

export const RouterLink = forwardRef(BaseRouterLink) as <
  T extends AnyProps = DefaultProps,
  K = unknown,
>(
  props: RouterLinkProps<T> & { ref?: Ref<K> },
) => ReactElement;

type NavigationLinkProps<T extends AnyProps> = RouterLinkProps<T> &
  Pick<NavLinkProps, 'end' | 'relative'>;

/**
 * Passes react router NavLink props to custom component
 *
 * Partially taken from:
 * https://github.com/remix-run/react-router/blob/main/packages/react-router-dom/index.tsx#L503
 */
const BaseNavigationLink = <T extends AnyProps, K = unknown>(
  { to, end, relative, component, ...restProps }: NavigationLinkProps<T>,
  forwardedRef: Ref<K>,
) => {
  const path = useResolvedPath(to, { relative });
  const location = useLocation();
  const locationPathname = location.pathname;
  const toPathname = path.pathname;

  const isActive =
    locationPathname === toPathname ||
    (!end &&
      locationPathname.startsWith(toPathname) &&
      locationPathname.charAt(toPathname.length) === '/');

  return (
    <RouterLink
      ref={forwardedRef}
      component={component}
      to={to}
      aria-current={isActive}
      {...(restProps as ExtractProps<T>)}
    />
  );
};

export const NavigationLink = forwardRef(BaseNavigationLink) as <
  T extends AnyProps = DefaultProps,
  K = unknown,
>(
  props: NavigationLinkProps<T> & { ref?: Ref<K> },
) => ReactElement;
